<script setup lang="ts" generic="T">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    /**
     * Används för att visualisera data för ett specifikt fält som satts via ett formulär (t.ex en opanmälan som skapats
     * via Operationsanmälan och sen visas i Operationsinformation).
     *
     * Arrayer som har strängar/nummer kommer automatiskt att renderas som en lista med spans. T.ex:
     *
     * <KFormFieldDisplay
     *   name="digitum"
     *   label="Digitum/Digitus"
     *   :value="operationsanmalan.digitum_display"
     *   content-direction="vertical"
     * />
     *
     * Värde som är sträng/nummer kommer automatiskt att renderas inom span. T.ex:
     *
     * <KFormFieldDisplay
     *   name="optid"
     *   label="Operationstid (min)"
     *   validation="required"
     *   :value="operationsanmalan.optid"
     * />
     *
     * Använd default slot för att rendera komplex data (arrayer med objekt/enskilt objekt). T.ex:
     *
     * <KFormFieldDisplay
     *   name="resurser"
     *   label="Resurser"
     *   class="resurser span-all"
     *   :value="operationsanmalan.resurser_display"
     *   content-direction="vertical"
     *   #default="{ value }"
     * >
     *   <span v-for="(item, index) in value" :key="index">
     *     {{ item.namn }}
     *   </span>
     * </KFormFieldDisplay>
     */

    import { getRequiredValidation, modifyRequiredValidation } from "@/components/forms/reusables/validationHelpers";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import { RenderDirection } from "@/components/forms/types";
    import { useStore } from "@/store";
    import { CONFIG_PROVIDE_NAME, ConfigType } from "@/store/modules/types/configModuleTypes";
    import { computed, inject, toRefs } from "vue";

    const store = useStore();

    export interface KFormFieldDisplayProps {
        name: string;
        /**
         * Vilken riktning ska griden gå åt?
         */
        direction?: RenderDirection;
        label?: string;
        /**
         * Samma typer av valideringsregler som Formulate (och senare Formkit)-komponenter tar emot. Används för att
         * t.ex avgöra om label ska visa *
         */
        validation?: string | Array<[rule: string, ...args: never]>;
        /**
         * Font weight på label
         */
        labelWeight?: "default" | "bold";
        /**
         * Mängd marginal mellan label och content
         */
        margin?: "default" | "small" | "large";
        value?: unknown;
        /**
         * Ska tomt värde visas? Visning av tomt värde innebär att "-" visas. False leder till att hela fältet döljs
         * vid tomt värde
         */
        showEmptyValue?: boolean;
        /**
         * Avgör riktning på grid flow. Användbart vid t.ex rendering av array
         */
        contentDirection?: RenderDirection;
        labelVariant?: "default" | "dark" | "blue";
        labelSize?: "default" | "large";
        align?: "default" | "center";
        showLineBreaks?: boolean;
    }

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>(CONFIG_PROVIDE_NAME);

    const props = withDefaults(defineProps<KFormFieldDisplayProps>(), {
        direction: RenderDirection.Vertical,
        contentDirection: RenderDirection.Horizontal,
        margin: "small",
        showEmptyValue: undefined,
        align: "default",
        showLineBreaks: true,
    });

    // Sätts oftast inom KFormDisplay. Värdet overridas om prop skickas in på den här specika komponenten
    const showEmptyValues: boolean | undefined = inject("showEmptyValues");

    const emptyValue = "–";

    const { name, validation, value, showEmptyValue, showLineBreaks } = toRefs(props);
    const modifiedValue = computed<any>(() => {
        if (typeof value?.value === "boolean") {
            // Konvertera till ja/nej
            return convertBooleanToString({ value: value.value });
        }

        // Sätt om till "tomt värde" om värde inte är satt
        if (typeof value?.value !== "number" && !value?.value) {
            return emptyValue;
        }

        // Sätt till tomt värde om arrayen är tom
        if (Array.isArray(value?.value) && !value.value.length) {
            return emptyValue;
        }

        return value?.value;
    });
    const modifiedShowEmptyValue = computed(() => {
        if (typeof showEmptyValues === "boolean" && typeof showEmptyValue?.value !== "boolean") {
            return showEmptyValues;
        }

        return showEmptyValue?.value;
    });

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
            type: ConfigType.Field,
        })
    );
    const shouldBeRequired = computed(() =>
        store.getters["config/shouldBeRequiredByName"]({
            defaultRequired: getRequiredValidation({ validation: validation?.value }),
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
        })
    );
    const modifiedValidation = computed(() =>
        modifyRequiredValidation({ shouldBeRequired: shouldBeRequired?.value, validation: validation?.value })
    );

    const convertBooleanToString = ({ value }: { value?: boolean }) => {
        return value ? "Ja" : "Nej";
    };
</script>
<template>
    <div
        v-if="
            shouldBeVisible &&
            (modifiedShowEmptyValue || (Array.isArray(value) && value.length) || (!Array.isArray(value) && value))
        "
        :class="['k-form-field-display', `${direction}`]"
    >
        <slot name="label">
            <KLabel
                v-if="label"
                :validation="modifiedValidation"
                :content="label"
                :margin="margin"
                :weight="labelWeight"
                :variant="labelVariant"
                :size="labelSize"
            />
        </slot>
        <div :class="['content', contentDirection, `align-${align}`]">
            <slot :value="modifiedValue" :showEmptyValue="modifiedShowEmptyValue" :emptyValue="emptyValue">
                <!-- Tom array eller ingen användning av slot default -->
                <template v-if="!Array.isArray(value) || !value?.length">
                    <span :class="{ 'line-breaks': showLineBreaks }">
                        {{ modifiedValue }}
                    </span>
                </template>
                <template v-else-if="Array.isArray(value) && value?.length">
                    <span v-for="(content, index) in value" :key="index">
                        {{ content }}
                    </span>
                </template>
            </slot>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .k-form-field-display {
        display: grid;
        grid-template-rows: min-content auto;

        &.horizontal {
            grid-auto-flow: column;
        }

        &.vertical {
            grid-auto-flow: row;
        }

        .content {
            display: grid;
            grid-auto-columns: auto;
            grid-column-gap: 1ch;

            &.horizontal {
                grid-auto-flow: column;
            }

            &.vertical {
                grid-auto-flow: row;
            }

            &.align-center {
                align-items: center;
            }

            .line-breaks {
                white-space: pre-line;
            }
        }

        label {
            margin-bottom: 0;
        }
    }
</style>
